import React, { FC, useEffect, useMemo, useState } from 'react'
import { Divider, Flex, Input, Select, Skeleton, Tooltip, Typography } from 'antd'
import { Button, EmptyState, Well } from '@pushly/aqe/lib/components'
import { CheckCircleOutlined, MinusCircleOutlined, SendOutlined, WarningOutlined } from '@ant-design/icons'
import { useService } from '@pushly/aqe/lib/hooks'
import { useThemeMode } from '@pushly/aqe'
import PreviewSDK, { Browser, Platform } from '@pushly/cuttlefish'
import { GenerateNotificationVariantsRequest } from '@pushly/models/lib/structs'
import { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { DomainService, NotificationService } from '../../../services'
import { DomainDto } from '../../../dtos/domain'
import { getClassNames } from '../../../_utils/classnames'
import { guaranteedDisplayRows } from './text-highlight'
import { useCompositeImageLoader } from '../../notification-builder/hooks/use-composite-image-loader'
// Not just the SDK call — this is the layout CSS `NotificationPreview` depends on to size the
// device mockup at all (fixed dock/wrapper positioning inside `.notification-preview__render`).
// Rendering `PreviewSDK.Preview` without it is what collapsed the panel to a sliver earlier.
import '../../../mobile-view/components/notification-preview/notification-preview.scss'

interface IBrandVoiceTestWell {
    domain: DomainDto
    /** The domain's current Brand Voice, so a generation can be read back against the exact same
     * rules the Directive well above is showing — not a copy of them. */
    compiled: CompiledDirective | null
    /** Whether a directive actually exists — NOT the same question as `compiled` being non-null,
     * which can also be null for a real, saved directive still mid-first-compile. Gates Generate:
     * "read back against every rule above" is a promise this well can't keep with nothing above
     * to read back against — see `deriveChecks`, whose Guaranteed row already reads as "nothing
     * to check" and whose Guidance row doesn't render at all when there's nothing set. */
    hasDirective: boolean
}

interface IGeneratedPreview {
    title: string
    body: string
    image?: string | null
}

/** A generation-time violation, narrowed to what the checklist needs to render one. */
interface IViolation {
    tier: 'guaranteed' | 'checked'
    reason: string
}

interface ICheckRow {
    status: 'pass' | 'fail' | 'neutral'
    text: string
}

const CHECK_ICON: Record<ICheckRow['status'], React.ReactNode> = {
    pass: <CheckCircleOutlined style={{ color: 'var(--ant-color-success)' }} />,
    fail: <WarningOutlined style={{ color: 'var(--ant-color-warning)' }} />,
    neutral: <MinusCircleOutlined style={{ color: 'var(--ant-color-text-tertiary)' }} />,
}

/**
 * Turns one generation's violations (server-authoritative — sailfish's own re-check of what it
 * just wrote) into the read-back checklist.
 *
 * Guaranteed is a real PASS/FAIL: a guaranteed rule with no violation against it held, because
 * the guarantee is a deterministic query sailfish itself evaluated. Guidance is NOT verified the
 * same way — `tier: 'checked'` violations still tell us a specific guidance statement was missed,
 * but the absence of a violation is not proof the rest were actively applied, only that none of
 * them tripped the checker. The pass row for Guidance is phrased as "read back against", not
 * "passed", to keep that honest.
 */
function deriveChecks(compiled: CompiledDirective | null, violations: IViolation[]): ICheckRow[] {
    const guaranteedRows = compiled ? guaranteedDisplayRows(compiled) : []
    const guidanceItems = [...(compiled?.assertions ?? []), ...(compiled?.style ?? []), ...(compiled?.unmapped ?? [])]

    const rows: ICheckRow[] = []

    if (guaranteedRows.length === 0) {
        rows.push({ status: 'neutral', text: 'Guaranteed — nothing to check.' })
    } else {
        const failedGuaranteed = violations.filter((v) => v.tier === 'guaranteed')
        if (failedGuaranteed.length === 0) {
            // A short confirmation, not the rules themselves — a real directive's guaranteed
            // sentences read back in full would routinely overflow a card this narrow, and the
            // full text is already one click away in the Directive well above.
            //
            // The count is dropped below 2: "All 1 guaranteed rule held" reads like a template
            // that forgot to pluralize, not like a number worth knowing. "All guaranteed rules
            // held" says the same thing without calling attention to the count for its own sake.
            rows.push({
                status: 'pass',
                text:
                    guaranteedRows.length < 2
                        ? 'All guaranteed rules held.'
                        : `All ${guaranteedRows.length} guaranteed rules held.`,
            })
        } else {
            rows.push(...failedGuaranteed.map((v): ICheckRow => ({ status: 'fail', text: v.reason })))
        }
    }

    if (guidanceItems.length > 0) {
        const failedGuidance = violations.filter((v) => v.tier === 'checked')
        // A violation only ever names what tripped, not which of the rest were actually
        // considered — so a clean read-back is reported as a count ("nothing flagged"), never as
        // a list of specific statements that "passed": nothing here confirms those individually.
        if (failedGuidance.length > 0) {
            rows.push(...failedGuidance.map((v): ICheckRow => ({ status: 'fail', text: v.reason })))
        } else {
            // Same reasoning as the Guaranteed row above: below 2, the count reads as a template
            // that forgot to pluralize rather than as a number worth knowing.
            rows.push({
                status: 'pass',
                text:
                    guidanceItems.length < 2
                        ? 'Guidance rules — nothing flagged.'
                        : `${guidanceItems.length} guidance rules — nothing flagged.`,
            })
        }
    }

    return rows
}

/**
 * "Rehearse" — the fastest way to see a Brand Voice actually applied, without leaving Settings
 * for the notification builder.
 *
 * Reuses the exact same generation call the builder makes (`generateNotificationVariants`), for a
 * single variant. Nothing here is ever sent, so the audience is just a technicality the request
 * shape requires — this always targets the domain's default "all subscribers" segment, with no
 * picker: for now, at least, there's no reason for an author checking their Brand Voice to care
 * which segment a generation nominally targets.
 *
 * One `platform` control governs both panels below it (the preview and, were the checklist ever
 * to become platform-sensitive, that too) — the same relationship the builder's own preview has
 * with its platform/state Selects, just hoisted above a two-column layout instead of living
 * inside a single reusable card.
 */
export const BrandVoiceTestWell: FC<IBrandVoiceTestWell> = ({ domain, compiled, hasDirective }) => {
    const domainId = domain.id
    const domainSvc = useService(DomainService)
    const notifSvc = useService(NotificationService)
    const { resolvedMode } = useThemeMode()
    // The API's `image` is a raw, possibly cross-origin source — not something an <img> can
    // necessarily load directly. The builder never shows it raw either; this is the same
    // fetch-crop-reupload step `usePushlyAi` runs before displaying a variant's image.
    const loadCompositeImage = useCompositeImageLoader({ id: domain.id, mediaQuality: domain.mediaQuality })

    const [platform, setPlatform] = useState<string>(Platform.MAC_OS)
    const [url, setUrl] = useState('')
    const [generating, setGenerating] = useState(false)
    const [result, setResult] = useState<IGeneratedPreview | null>(null)
    const [violations, setViolations] = useState<IViolation[]>([])
    const [error, setError] = useState<string | null>(null)

    const [segmentsLoading, setSegmentsLoading] = useState(true)
    const [segmentId, setSegmentId] = useState<number | null>(null)

    useEffect(() => {
        let cancelled = false
        setSegmentsLoading(true)
        domainSvc.fetchSegmentsByDomainId(domainId).then((res) => {
            if (cancelled) return
            const list = res.data ?? []
            const defaultSegment = list.find((s) => s.isDefault) ?? list[0]
            setSegmentId(defaultSegment?.id ?? null)
            setSegmentsLoading(false)
        })
        return () => {
            cancelled = true
        }
    }, [domainId])

    const handleGenerate = async () => {
        const landingUrl = url.trim()
        if (!landingUrl || generating || !segmentId) return

        setGenerating(true)
        setError(null)
        setViolations([])
        setResult(null)

        try {
            const req = GenerateNotificationVariantsRequest.from({
                flow: 'legacy',
                domain_id: domainId,
                segment_id: segmentId,
                // Just the one: this panel only ever shows a single result, and requesting more
                // would mean either reconciling `policyError.violations` against whichever
                // `variant_index` happens to be displayed (every violation carries one — see
                // below) or paying for three generations nobody sees. Neither is worth it for a
                // single-preview tool, and cost is genuinely lower this way — 4 variants is 4x
                // the generation, not one generation split four ways.
                num_variations: 1,
                fewshot_max_days: 7,
                content: { landing_url: landingUrl },
                // Always fresh: an author using this to check a Brand Voice edit is here
                // specifically to see the CURRENT directive applied — a cached response from
                // before their edit would look like the edit didn't take.
                skip_cache: true,
                assertion_checks: [],
                prior_violations: [],
            })

            const res = await notifSvc.generateNotificationVariants(domainId, req, { showLoadingScreen: false })

            if (res.data?.error) {
                setError(res.data.error.message || 'That URL could not be analyzed.')
                return
            }

            // Scoped to variant 0 specifically, not "any violation this response carries" — with
            // a single variant that's currently everything, but a violation is intrinsically
            // about one specific variant (see `variant_index` on the struct), and reading the
            // whole array without that filter is exactly what showed a failure next to a preview
            // it had nothing to do with when this used to request 4.
            if (res.data?.policyError?.violations?.length) {
                setViolations(
                    res.data.policyError.violations
                        .filter((v) => v.variant_index === 0)
                        .map((v) => ({ tier: v.tier, reason: v.reason })),
                )
            }

            const variant = res.data?.variants?.[0]
            if (!res.ok || !variant) {
                setError('Nothing generated. Try a different URL.')
                return
            }

            // Best-effort: a failed composite falls back to the raw URL rather than dropping the
            // image entirely — same trade `usePushlyAi` makes.
            let image = variant.image ?? null
            if (image) {
                try {
                    const composite = await loadCompositeImage(image)
                    image = composite?.url ?? image
                } catch {
                    // keep the raw URL
                }
            }

            setResult({ title: variant.title ?? '', body: variant.body ?? '', image })
        } catch {
            setError('Something went wrong generating a preview.')
        } finally {
            setGenerating(false)
        }
    }

    const checks = useMemo(() => (result ? deriveChecks(compiled, violations) : []), [result, compiled, violations])

    return (
        <Well title="Preview Brand Voice" footerMode="hidden" shadow={true}>
            <Flex vertical gap={12}>
                <Flex gap={8}>
                    <Input
                        placeholder="https://your-domain.com/an-article"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        onPressEnter={handleGenerate}
                        disabled={generating}
                    />
                    <Tooltip
                        title={
                            hasDirective ? undefined : 'Set up your Brand Voice above before rehearsing a generation.'
                        }
                    >
                        {/* A disabled antd Button doesn't fire pointer events, which is also what
                            stops a Tooltip from ever showing on hover over it — wrapping in a
                            plain span gives the Tooltip something that still receives them. */}
                        <span>
                            <Button
                                type="primary"
                                onClick={handleGenerate}
                                disabled={!hasDirective || !url.trim() || generating || segmentsLoading || !segmentId}
                            >
                                <SendOutlined />
                                <span>Generate</span>
                            </Button>
                        </span>
                    </Tooltip>
                </Flex>

                {/* The outer cap on the result area — the Preview/Checklist columns below already
                    scroll independently within their own max-heights, but this is the ceiling on
                    the two of them together, so the well itself can never grow past a predictable
                    footprint no matter how tall either column's content gets. */}
                <div className={getClassNames('bvw-test-result')} style={{ maxHeight: 560 }}>
                    {generating ? (
                        <Skeleton active title={false} paragraph={{ rows: 3 }} />
                    ) : !segmentsLoading && !segmentId ? (
                        <EmptyState
                            size="small"
                            icon={<WarningOutlined />}
                            title="No segment to generate against"
                            description="This domain has no segments yet, so there's nothing to rehearse a send to."
                        />
                    ) : error ? (
                        <EmptyState
                            size="small"
                            icon={<WarningOutlined />}
                            title="Couldn't generate a preview"
                            description={error}
                        />
                    ) : result ? (
                        <Flex gap={16} align="flex-start" className={getClassNames('bvw-columns')}>
                            {/* Fixed at cuttlefish's own Windows dimensions (380px env-wrapper +
                                its device markup), not the shared shrinkable `.bvw-column` class:
                                that class's `flex: 1 1 360px; min-width: 0` let this column shrink
                                below cuttlefish's fixed-pixel device markup (300–400px depending on
                                platform), which is what actually caused the "compressed" look —
                                `chromeless: true` gives us "no device shell" for free, but it also
                                drops cuttlefish's own width/height rule for the platform wrapper,
                                so an ancestor has to supply real sizing instead of relying on it. */}
                            <Flex style={{ flex: '0 0 460px', minWidth: 0 }}>
                                <Well
                                    variant="ghost"
                                    padding="small"
                                    titleSize="sm"
                                    style={{ width: '100%' }}
                                    title={<Typography.Text strong>Preview</Typography.Text>}
                                    actions={
                                        <Select
                                            size="small"
                                            value={platform}
                                            onChange={setPlatform}
                                            options={[
                                                {
                                                    label: 'Desktop',
                                                    title: 'Desktop',
                                                    options: [
                                                        { value: Platform.MAC_OS, label: 'macOS' },
                                                        { value: Platform.WINDOWS, label: 'Windows' },
                                                    ],
                                                },
                                                {
                                                    label: 'Mobile',
                                                    title: 'Mobile',
                                                    options: [
                                                        { value: Platform.ANDROID, label: 'Android' },
                                                        { value: Platform.IOS, label: 'iOS' },
                                                    ],
                                                },
                                            ]}
                                            style={{ width: 110 }}
                                        />
                                    }
                                >
                                    {/* A FIXED width and height, not just fixed width — the
                                        column's own width alone doesn't stop each platform's
                                        differently-sized card from rendering at its own intrinsic
                                        box and just sitting off-center inside a same-width but
                                        differently-TALL frame. Both axes centered (`alignItems` +
                                        `justifyContent`), and `overflow: hidden` as a hard backstop:
                                        if a platform's card is ever taller/wider than this frame
                                        (a cuttlefish quirk we haven't hit yet, the way macOS's
                                        `.expanded` height was), it gets cropped rather than
                                        resizing the frame around it — the whole point is that this
                                        box never changes size, regardless of what's inside it. */}
                                    <div
                                        style={{
                                            background: 'var(--ant-color-fill-quaternary)',
                                            borderRadius: 'var(--ant-border-radius-lg, 8px)',
                                            width: 420,
                                            height: 380,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <div
                                            className="notification-preview__render"
                                            style={{
                                                isolation: 'isolate',
                                                contain: 'layout paint style',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <PreviewSDK.Preview
                                                config={{
                                                    animated: false,
                                                    browser: Browser.CHROME,
                                                    platform,
                                                    state: 'expanded',
                                                    chromeless: true,
                                                    appName: domain.displayName,
                                                    systemMode: resolvedMode ?? 'light',
                                                    notification: {
                                                        title: result.title || 'Untitled',
                                                        body: result.body || '',
                                                        image: result.image ?? undefined,
                                                        domain: domain.name,
                                                    },
                                                }}
                                            />
                                        </div>
                                    </div>
                                </Well>
                            </Flex>

                            <Divider orientation="vertical" className="inner-well-divider vertical" />

                            <Flex className={getClassNames('bvw-column')}>
                                <Well
                                    variant="ghost"
                                    padding="small"
                                    titleSize="sm"
                                    style={{ width: '100%' }}
                                    // Scrolls independently of the Preview column beside it — a
                                    // long checklist (many guidance items, several violations)
                                    // must never resize the card the reader is comparing it
                                    // against, matching the same fixed-height-while-collapsed
                                    // treatment the Directive well's columns already use.
                                    bodyStyle={{ maxHeight: 420 }}
                                    title={<Typography.Text strong>How it read against your rules</Typography.Text>}
                                >
                                    <Flex vertical gap={8}>
                                        {checks.map((check, i) => (
                                            <Flex key={i} align="flex-start" gap={8}>
                                                <span style={{ marginTop: 2 }}>{CHECK_ICON[check.status]}</span>
                                                <Typography.Text>{check.text}</Typography.Text>
                                            </Flex>
                                        ))}
                                    </Flex>
                                </Well>
                            </Flex>
                        </Flex>
                    ) : !hasDirective ? (
                        <EmptyState
                            size="small"
                            title="Set up your Brand Voice first"
                            description="There's nothing above to read a generation back against yet — set up your Brand Voice, then come back here to rehearse it."
                        />
                    ) : (
                        <EmptyState
                            size="small"
                            title="Nothing generated yet"
                            description="Paste an article this domain has published and generate a notification from it. The result is read back against every rule above."
                        />
                    )}
                </div>
            </Flex>
        </Well>
    )
}
